// src/app/layout/topbar/topbar.component.ts
import { Component, EventEmitter, Output, Input, signal, computed, inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { SolicitudesPendientesService } from '../../features/servicio-tecnico/services/solicitudes-pendientes.service';
import { SolicitudesWebPendientesService } from '../../features/servicio-tecnico/services/solicitudes-web-pendientes.service';
import { CotizacionesPorValorarService } from '../../features/cotizaciones/services/cotizaciones-por-valorar.service';
import { OrdenesEnRecepcionService } from '../../features/servicio-tecnico/services/ordenes-en-recepcion.service';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { PopoverModule, Popover } from 'primeng/popover';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FormsModule } from '@angular/forms';
import {
  LucideAngularModule, LucideIconData,
  Menu, Sun, Moon, Bell, BellOff, ChevronDown, User, Settings, LogOut,
  ArrowLeftRight, AlertCircle, Clock, CalendarX, File, FilePen, Hourglass, Truck, Package, TriangleAlert, Info, Search,
  Keyboard,
} from 'lucide-angular';
import { RecordNavService } from '../../core/navegacion-registros/record-nav.service';
import { Subscription } from 'rxjs';
import { AuthService } from '../../features/auth/services/auth.service';
import { ThemeService } from '../../core/services/theme.service';
import { EmpresaLogoService } from '../../core/services/empresa-logo.service';
import { NotificacionAppService, NotificacionResponse } from '../../core/services/notificacion-app.service';
import { TimeAgoPipe } from '../../shared/pipes/time-ago.pipe';
import { SrCommandPaletteComponent } from '../../shared/components/sr-command-palette/sr-command-palette.component';
import { MaquiUiService } from '../../core/services/maqui-ui.service';
import { ExpedienteOfflineService } from '../../core/offline/expediente-offline.service';
import { SrModalComponent } from '../../shared/components/sr-modal/sr-modal.component';
import { OperadorService } from '../../core/operador/operador.service';
import { CierreSesionService } from '../services/cierre-sesion.service';
import { InstanceConfigService } from '../../core/services/instance-config.service';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    PopoverModule,
    BadgeModule,
    AvatarModule,
    ToggleSwitchModule,
    TooltipModule,
    ProgressSpinnerModule,
    LucideAngularModule,
    TimeAgoPipe,
    SrCommandPaletteComponent,
    SrModalComponent
  ],
  template: `
    <div class="layout-topbar" [class.topbar-mobile]="isMobile">
      <div class="topbar-start">
        @if (!isMobile) {
          <button
            pButton
            type="button"
            class="p-button-text p-button-rounded"
            aria-label="Menu"
            (click)="onToggleSidebar()"
            pTooltip="Menu"
            tooltipPosition="bottom">
            <lucide-angular [img]="icons.Menu" [size]="20"></lucide-angular>
          </button>
        }

        <div class="topbar-logo" (click)="navigateTo(esPlataforma() ? '/admin/plataforma' : '/dashboard')">
          <!-- El panel es nuestra propia cuenta: va la marca de SoftRent y el dominio, para saber en qué entorno
               se está operando (mockup aprobado el 29-oct). -->
          @if (esPlataforma()) {
            <img src="assets/brand/softrent-mark.png" alt="" class="topbar-brand__mark" width="28" height="28">
            <span class="topbar-brand__name">Soft<span class="topbar-brand__acento">Rent</span></span>
            @if (!isMobile) { <span class="topbar-brand__env">{{ dominio }}</span> }
          } @else if (logoService.hasCustomLogo()) {
            <img [src]="logoService.logoUrl()" alt="Logo empresa" class="topbar-logo-img">
          } @else {
            <span class="logo-text">{{ empresaNombre || 'Mi Empresa' }}</span>
          }
        </div>

        <!-- Buscador global (⌘K). En escritorio el nombre es el texto que se ve
             y el atajo va en aria-keyshortcuts: un aria-label distinto del texto
             visible deja sin efecto a quien lo dicta por voz («clic en Buscar»). -->
        <!-- En el panel no hay qué buscar: su base no tiene clientes, equipos ni documentos. -->
        @if (!esPlataforma()) {
        <button type="button" class="topbar-search" (click)="cmdk.openPalette()"
                [attr.aria-label]="isMobile ? 'Buscar' : null" aria-keyshortcuts="Control+K">
          <lucide-angular [img]="icons.Search" [size]="16"></lucide-angular>
          @if (!isMobile) {
            <span class="topbar-search__ph">Buscar…</span>
            <span class="topbar-search__kbd" aria-hidden="true"><kbd>Ctrl</kbd><kbd>K</kbd></span>
          }
        </button>
        }

        <!-- Launcher de Maqui (#648) — solo si está habilitado -->
        @if (maquiUi.enabled()) {
          <button type="button" class="topbar-maqui" (click)="maquiUi.abrir()"
                  aria-label="Pregúntale a Maqui">
            <img class="topbar-maqui__img" src="assets/maqui/casquito.png" alt="Maqui" />
            @if (!isMobile) { <span class="topbar-maqui__label">Maqui</span> }
          </button>
        }
      </div>

      <div class="topbar-end">
        <!-- Theme Switcher -->
        <div class="theme-switch" [class.theme-switch-mobile]="isMobile">
          <lucide-angular [img]="isDarkMode ? icons.Moon : icons.Sun" [size]="17" class="theme-icon"></lucide-angular>
          <p-toggleSwitch
            [(ngModel)]="isDarkMode"
            (onChange)="toggleTheme()"
            ariaLabel="Modo oscuro">
          </p-toggleSwitch>
        </div>

        <!-- Atajos de la navegación entre registros (J/K/U/?): solo en un detalle abierto desde una lista -->
        @if (!isMobile && recordNav.activo()) {
          <button pButton type="button" class="p-button-text p-button-rounded topbar-atajos"
                  aria-label="Atajos de teclado (?)" aria-keyshortcuts="?"
                  pTooltip="Atajos de teclado · ?" tooltipPosition="bottom"
                  (click)="recordNav.mostrarAtajos.set(true)">
            <lucide-angular [img]="icons.Keyboard" [size]="18"></lucide-angular>
          </button>
        }

        <!-- Notificaciones (ocultas en mobile, van al menu "Mas") -->
        @if (!isMobile) {
        <div class="notification-bell" (click)="toggleNotificaciones($event)">
          <button
            pButton
            type="button"
            class="p-button-text p-button-rounded"
            aria-label="Notificaciones"
            [class.bell-active]="cantidadNoLeidas > 0"
            pTooltip="Notificaciones"
            tooltipPosition="bottom">
            <lucide-angular [img]="icons.Bell" [size]="18"></lucide-angular>
          </button>
          @if (cantidadNoLeidas > 0) {
            <span class="notification-badge">
              {{ cantidadNoLeidas > 99 ? '99+' : cantidadNoLeidas }}
            </span>
          }
        </div>

        <!-- Notifications Panel -->
        <p-popover #notifPanel styleClass="notif-popover" appendTo="body">
          <div class="notif-panel">
            <div class="notif-header">
              <h4>Notificaciones</h4>
              @if (cantidadNoLeidas > 0) {
                <button pButton label="Marcar todas leídas"
                  class="p-button-text p-button-sm"
                  (click)="marcarTodasLeidas()">
                </button>
              }
            </div>

            <div class="notif-list">
              @if (cargandoNotificaciones) {
                <div class="notif-loading">
                  <p-progressSpinner strokeWidth="4" [style]="{'width': '32px', 'height': '32px'}"></p-progressSpinner>
                  <span>Cargando...</span>
                </div>
              } @else if (notificaciones.length === 0) {
                <div class="notif-empty">
                  <lucide-angular [img]="icons.BellOff" [size]="28"></lucide-angular>
                  <span>Sin notificaciones pendientes</span>
                </div>
              } @else {
                @for (notif of notificaciones; track notif.id) {
                  <div class="notif-item" [class.notif-unread]="!notif.leida"
                       (click)="abrirNotificacion(notif)">
                    <div class="notif-icon">
                      <lucide-angular [img]="getIconData(notif)" [size]="20" [style.color]="getIconColor(notif)"></lucide-angular>
                    </div>
                    <div class="notif-content">
                      <span class="notif-title">{{ notif.titulo }}</span>
                      <span class="notif-message">{{ notif.mensaje }}</span>
                      <span class="notif-time">{{ notif.fechaCreacion | timeAgo }}</span>
                    </div>
                  </div>
                }
              }
            </div>
          </div>
        </p-popover>
        }

        <!-- Usuario -->
        <button
          pButton
          type="button"
          class="p-button-text user-button"
          (click)="userPanel.toggle($event)">
          <p-avatar
            [label]="userInitials()"
            shape="circle"
            class="avatar-user">
          </p-avatar>
          @if (!isMobile) {
            <span class="user-name">{{ userName() }}</span>
            <lucide-angular [img]="icons.ChevronDown" [size]="16"></lucide-angular>
          }
        </button>

        <!-- User Menu Popover -->
        <p-popover
          #userPanel
          showCloseIcon="false"
          styleClass="user-menu-popover">
          <div class="user-menu">
            <div class="user-menu-header">
              <p-avatar
                [label]="userInitials()"
                size="large"
                shape="circle"
                class="avatar-user">
              </p-avatar>
              <div class="user-info">
                <div class="user-info-name">{{ userName() }}</div>
                <div class="user-info-email">{{ mecanico() ? (mecanico()!.cargo || 'Mecánico') : (currentUser()?.email || '') }}</div>
              </div>
            </div>

            <div class="menu-divider"></div>

            <div class="user-menu-items">
              <button
                type="button"
                class="menu-item"
                (click)="navigateTo('/perfil'); userPanel.hide()">
                <lucide-angular [img]="icons.User" [size]="16"></lucide-angular>
                <span>Mi Perfil</span>
              </button>

              <!-- Mecánico por PIN: quien tiene la tablet es la persona del menú; «Cambiar» vive acá, no en otro botón de la barra. -->
              @if (mecanico()) {
                <button
                  type="button"
                  class="menu-item"
                  (click)="operador.cambiar(); userPanel.hide()">
                  <lucide-angular [img]="icons.ArrowLeftRight" [size]="16"></lucide-angular>
                  <span>Cambiar de mecánico</span>
                </button>
              }

              <!-- Las cuentas de terreno (técnico, operador) no tienen configuración: la ruta las rebotaría. -->
              @if (!esDeTerreno) {
                <button
                  type="button"
                  class="menu-item"
                  (click)="navigateTo('/configuracion'); userPanel.hide()">
                  <lucide-angular [img]="icons.Settings" [size]="16"></lucide-angular>
                  <span>Configuracion</span>
                </button>
              }

              <div class="menu-divider"></div>

              <button
                type="button"
                class="menu-item menu-item-danger"
                (click)="logout(); userPanel.hide()">
                <lucide-angular [img]="icons.LogOut" [size]="16"></lucide-angular>
                <span>Cerrar Sesion</span>
              </button>
            </div>
          </div>
        </p-popover>
      </div>

      <!-- Buscador global (⌘K) — overlay montado una vez, escucha el atajo globalmente -->
      <sr-command-palette #cmdk></sr-command-palette>
    </div>

    <sr-modal [(visible)]="salidaTabletOpen" title="¿Cerrar la sesión de la tablet?" size="sm" variant="danger"
      [icon]="icons.LogOut" confirmText="Cerrar sesión de la tablet" (confirm)="confirmarSalidaDeLaTablet()">
      <p>Después nadie podrá entrar con su PIN hasta que alguien ingrese con el usuario y la contraseña de la tablet.</p>
      <p>Si solo quieres pasarle la tablet a otro mecánico, usa «Cambiar de mecánico».</p>
    </sr-modal>

    <sr-modal [(visible)]="logoutDialogOpen" title="Cambios sin sincronizar"
      [icon]="icons.TriangleAlert" size="sm">
      @if (totalCambiosPendientes() > 0) {
        <p>Hay {{ totalCambiosPendientes() }} {{ totalCambiosPendientes() === 1 ? 'cambio pendiente' : 'cambios pendientes' }} de sincronizar en este dispositivo.</p>
      } @else if ((offlineSummary()?.borradores ?? 0) > 0) {
        <p>Tienes borradores guardados que todavía no se han enviado.</p>
      }
      @if ((offlineSummary()?.rechazadas ?? 0) > 0) {
        <p>{{ offlineSummary()?.rechazadas }} {{ (offlineSummary()?.rechazadas ?? 0) === 1 ? 'cambio fue rechazado' : 'cambios fueron rechazados' }} y necesita{{ (offlineSummary()?.rechazadas ?? 0) === 1 ? '' : 'n' }} revisión.</p>
      }
      @if ((offlineSummary()?.fotos ?? 0) > 0) {
        <p>Incluyen {{ offlineSummary()?.fotos }} {{ (offlineSummary()?.fotos ?? 0) === 1 ? 'foto' : 'fotos' }} sin enviar.</p>
      }
      @if ((offlineSummary()?.borradores ?? 0) > 0) {
        <p>Hay {{ offlineSummary()?.borradores }} {{ (offlineSummary()?.borradores ?? 0) === 1 ? 'borrador guardado' : 'borradores guardados' }} en esta tablet.</p>
      }
      @if (logoutMessage()) {
        <p class="logout-offline-message" role="status">{{ logoutMessage() }}</p>
      }
      <div footer class="logout-offline-actions">
        <button type="button" class="logout-offline-sync" [disabled]="logoutSyncing() || !hayCambiosPorSincronizar()" (click)="sincronizarYSalir()">
          @if (logoutSyncing()) { Sincronizando… } @else { Sincronizar y salir }
        </button>
        <button type="button" class="logout-offline-continue" [disabled]="logoutSyncing()" (click)="seguirTrabajando()">Seguir trabajando</button>
        <button type="button" class="logout-offline-force" [disabled]="logoutSyncing()" (click)="salirConPendientes()">Salir de todas formas</button>
      </div>
    </sr-modal>
  `,
  styles: [`
    .topbar-logo {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      cursor: pointer;
      user-select: none;
    }

    /* Marca de SoftRent en el panel de plataforma */
    .topbar-brand__mark { width: 28px; height: 28px; flex-shrink: 0; }
    .topbar-brand__name {
      font-size: var(--fs-lg);
      font-weight: var(--fw-bold);
      letter-spacing: var(--ls-title);
      color: var(--ink);
      white-space: nowrap;
    }
    .topbar-brand__acento { color: var(--accent-text); }
    .topbar-brand__env {
      font-family: var(--font-mono);
      font-size: var(--fs-xs);
      color: var(--ink-mute);
      background: var(--paper-tint);
      border: 1px solid var(--hairline);
      border-radius: var(--radius-pill);
      padding: 0.0625rem 0.5rem;
      white-space: nowrap;
    }

    /* Trigger del buscador global (⌘K) */
    .topbar-search {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-left: 0.75rem;
      padding: 0.4rem 0.75rem;
      min-width: 40px;
      border: 1px solid var(--hairline, #e7e2dc);
      border-radius: var(--radius-pill, 999px);
      background: var(--surface-ground, #faf8f5);
      color: var(--ink-mute, #8a8178);
      cursor: pointer;
      transition: border-color .15s ease, color .15s ease, background .15s ease;
      font-family: inherit;
    }
    .topbar-search:hover {
      border-color: var(--accent, #E55408);
      color: var(--ink, #1a1a1a);
    }
    .topbar-search__ph { font-size: 13px; }
    .topbar-search__kbd { display: inline-flex; gap: 2px; margin-left: 0.35rem; }
    .topbar-search__kbd kbd {
      font-size: 10px; line-height: 1; padding: 2px 4px; font-family: inherit;
      border: 1px solid var(--hairline, #e7e2dc); border-radius: 4px;
      background: var(--surface-card, #fff); color: var(--ink-faint, #b3aaa0);
    }
    @media (max-width: 640px) {
      .topbar-search { margin-left: 0.35rem; padding: 0.4rem; }
    }

    /* Launcher de Maqui (#648) — pill junto al buscador */
    .topbar-maqui {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      margin-left: 0.5rem;
      padding: 0.25rem 0.6rem 0.25rem 0.3rem;
      border: 1px solid var(--hairline, #e7e2dc);
      border-radius: var(--radius-pill, 999px);
      background: var(--surface-ground, #faf8f5);
      color: var(--ink-2, #3a352d);
      cursor: pointer;
      transition: border-color .15s ease, background .15s ease, color .15s ease;
      font-family: inherit;
    }
    .topbar-maqui:hover {
      border-color: var(--accent, #E55408);
      background: color-mix(in srgb, var(--accent, #E55408) 8%, transparent);
      color: var(--accent-deep, #A83A05);
    }
    .topbar-maqui__img {
      width: 22px; height: 22px; border-radius: 999px;
      object-fit: cover; transform: scale(1.22);
      background: #fff; border: 1px solid var(--hairline, #e7e2dc);
    }
    .topbar-maqui__label { font-size: 13px; font-weight: 600; }
    @media (max-width: 640px) {
      .topbar-maqui { margin-left: 0.35rem; padding: 0.25rem; }
    }

    .topbar-logo-img {
      height: 32px;
      max-width: 120px;
      object-fit: contain;
    }

    .topbar-logo-icon {
      width: 32px;
      height: 32px;
      border-radius: var(--radius-md);
      background: var(--primary-color);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: var(--fw-bold);
      font-size: var(--fs-2xs);
      letter-spacing: var(--ls-title);
    }

    .notification-bell {
      position: relative;
      display: inline-flex;
    }

    .notification-bell .bell-active {
      color: var(--primary-color);
    }

    .notification-badge {
      /* Token v16 --danger en vez de red-500 legacy (mejor consistencia DS),
         altura reducida 18px → 16px + padding ajustado para que el "99+"
         siga cabiendo sin recortarse. line-height: 1 + flex center evita
         offset baseline. */
      position: absolute;
      top: 2px;
      right: 2px;
      background: var(--danger);
      color: white;
      border-radius: 10px;
      font-size: var(--fs-2xs);
      font-weight: var(--fw-bold);
      min-width: 18px;
      height: 16px;
      line-height: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0 5px;
      pointer-events: none;
    }

    /* El panel de notificaciones (appendTo="body") se estiliza globalmente en
       styles.scss (.notif-popover). Ver ahí los estilos v16. */

    .theme-switch-mobile {
      border: none !important;
      padding: 0 0.5rem !important;
    }

    .logout-offline-message { color: var(--danger); font-weight: var(--fw-medium); }
    .logout-offline-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 0.5rem; }
    .logout-offline-actions button {
      min-height: var(--control-h); padding: 0 0.75rem; border: 1px solid var(--hairline);
      border-radius: var(--radius-md); background: var(--surface-card); color: var(--ink-2);
      font: inherit; font-weight: var(--fw-medium); cursor: pointer;
    }
    .logout-offline-actions button:hover:not(:disabled) { border-color: var(--accent); color: var(--accent-text); }
    .logout-offline-actions button:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
    .logout-offline-actions button:disabled { opacity: 0.55; cursor: not-allowed; }
    .logout-offline-actions .logout-offline-sync { background: var(--accent); border-color: var(--accent); color: var(--on-accent); }
    .logout-offline-actions .logout-offline-sync:hover:not(:disabled) { background: var(--accent-hover); color: var(--on-accent); }
    .logout-offline-actions .logout-offline-force { color: var(--danger); }

    :host ::ng-deep .p-avatar.avatar-user {
      background: linear-gradient(135deg, var(--accent-color), var(--accent-color-hover));
      box-shadow: 0 2px 8px color-mix(in srgb, var(--accent) 30%, transparent);
      color: var(--on-accent);

      .p-avatar-text {
        color: var(--on-accent);
        font-weight: var(--fw-bold);
      }
    }

    /* === RESPONSIVE === */
    @media (max-width: 767px) {
      .topbar-logo-img {
        height: 28px;
        max-width: 100px;
      }

      /* .notif-popover responsive vive en styles.scss (global, appendTo=body). */
      :host ::ng-deep .user-menu-popover {
        width: 90vw;
      }
    }
  `]
})
export class TopbarComponent implements OnInit, OnDestroy {
  @Output() toggleSidebar = new EventEmitter<void>();
  @Input() sidebarCollapsed = false;
  @Input() isMobile = false;
  @ViewChild('notifPanel') notifPanel!: Popover;

  private authService = inject(AuthService);
  private themeService = inject(ThemeService);
  private router = inject(Router);
  private solicitudesPendientes = inject(SolicitudesPendientesService);
  private solicitudesWebPendientes = inject(SolicitudesWebPendientesService);
  private cotizacionesPorValorar = inject(CotizacionesPorValorarService);
  private ordenesEnRecepcion = inject(OrdenesEnRecepcionService);
  private notificacionService = inject(NotificacionAppService);
  private offline = inject(ExpedienteOfflineService);
  protected operador = inject(OperadorService);
  private cierreSesion = inject(CierreSesionService);
  protected readonly esDeTerreno = inject(AuthService).isFieldAccount();
  logoService = inject(EmpresaLogoService);
  maquiUi = inject(MaquiUiService);
  protected readonly recordNav = inject(RecordNavService);
  private readonly instanceConfig = inject(InstanceConfigService);
  /** La cuenta de plataforma (admin.softrent.cl): marca propia y sin buscador. */
  protected readonly esPlataforma = this.instanceConfig.esPanelPlataforma;
  /** El dominio en que se está operando: admin.softrent.cl o el de QAS. */
  protected readonly dominio = window.location.hostname;

  // Iconos Lucide (barrido v16 #353 · topbar migrado de PrimeIcons a Lucide).
  readonly icons = {
    Menu, Sun, Moon, Bell, BellOff, ChevronDown, User, Settings, LogOut,
    ArrowLeftRight, AlertCircle, Clock, CalendarX, File, FilePen, Hourglass, Truck, Package, TriangleAlert, Info, Search,
    Keyboard,
  };

  currentUser = signal<any>(null);
  readonly logoutDialogOpen = signal(false);
  readonly salidaTabletOpen = signal(false);
  readonly logoutSyncing = signal(false);
  readonly offlineSummary = signal<{ pendientes: number; fotos: number; rechazadas: number; borradores: number } | null>(null);
  readonly logoutMessage = signal('');
  cantidadNoLeidas = 0;
  notificaciones: NotificacionResponse[] = [];
  cargandoNotificaciones = false;
  isDarkMode: boolean = false;
  empresaNombre: string = '';

  private subs: Subscription[] = [];

  ngOnInit() {
    this.currentUser.set(this.authService.getUserData());
    this.empresaNombre = this.currentUser()?.empresaNombre || '';
    this.subs.push(
      this.themeService.isDarkMode().subscribe(isDark => {
        this.isDarkMode = isDark;
      })
    );

    // El «Más» de la barra inferior del panel también cierra sesión: por el mismo camino que el menú de usuario.
    this.subs.push(this.cierreSesion.pedido$.subscribe(() => void this.logout()));

    this.notificacionService.iniciarPolling();
    this.subs.push(
      this.notificacionService.getCantidadNoLeidas().subscribe(count => {
        this.cantidadNoLeidas = count;
      })
    );
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
    this.notificacionService.detenerPolling();
  }

  toggleNotificaciones(event: Event) {
    this.notifPanel.toggle(event);
    // Siempre recargar al abrir para mostrar datos frescos
    this.cargarNotificaciones();
  }

  cargarNotificaciones() {
    this.cargandoNotificaciones = true;
    this.notificacionService.obtenerNoLeidas().subscribe({
      next: (response) => {
        this.cargandoNotificaciones = false;
        if (response.success) {
          this.notificaciones = response.data || [];
        }
      },
      error: () => {
        this.cargandoNotificaciones = false;
      }
    });
  }

  abrirNotificacion(notif: NotificacionResponse) {
    if (!notif.leida) {
      this.notificacionService.marcarComoLeida(notif.id).subscribe(() => {
        this.notificacionService.actualizarContador();
        // Remover de la lista de no leídas
        this.notificaciones = this.notificaciones.filter(n => n.id !== notif.id);
      });
      this.cantidadNoLeidas = Math.max(0, this.cantidadNoLeidas - 1);
    }

    if (notif.enlace) {
      this.router.navigate([notif.enlace]);
      this.notifPanel.hide();
    }
  }

  marcarTodasLeidas() {
    this.notificacionService.marcarTodasComoLeidas().subscribe(() => {
      this.notificaciones = [];
      this.cantidadNoLeidas = 0;
      this.notificacionService.actualizarContador();
    });
  }

  getIconData(notif: NotificacionResponse): LucideIconData {
    switch (notif.tipo) {
      case 'FACTURA_VENCIDA': return this.icons.AlertCircle;
      case 'FACTURA_POR_VENCER': return this.icons.Clock;
      case 'CONTRATO_POR_VENCER': return this.icons.CalendarX;
      case 'EP_PENDIENTE': return this.icons.File;
      case 'EP_SIN_FACTURAR': return this.icons.FilePen;
      case 'EP_SIN_APROBAR': return this.icons.Hourglass;
      case 'GUIA_EN_TRANSITO': return this.icons.Truck;
      case 'STOCK_BAJO': return this.icons.Package;
      case 'ALERTA': return this.icons.TriangleAlert;
      default: return this.icons.Info;
    }
  }

  getIconColor(notif: NotificacionResponse): string {
    switch (notif.severidad) {
      case 'ERROR': return 'var(--danger)';
      case 'WARN': return 'var(--warning)';
      default: return 'var(--accent)';
    }
  }

  onToggleSidebar() {
    this.toggleSidebar.emit();
  }

  toggleTheme() {
    this.themeService.toggleDarkMode();
  }

  /** El mecánico que tiene la tablet (si el taller usa PIN): es la persona que se muestra, en lugar de la cuenta compartida. */
  mecanico = computed(() => this.operador.listo() ? this.operador.operador() : null);

  userName(): string {
    const u = this.currentUser();
    return this.mecanico()?.nombre || this.nombreDe(u) || u?.username || 'Usuario';
  }

  userInitials(): string {
    const mecanico = this.mecanico();
    if (mecanico) {
      return mecanico.nombre.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || 'M';
    }
    const user = this.currentUser();
    const nombre = this.nombreDe(user);
    if (nombre) {
      return nombre.split(/\s+/).filter(Boolean).slice(0, 2).map((p: string) => p[0]?.toUpperCase()).join('');
    }
    if (!user || !user.username) return 'U';
    return user.username.substring(0, 2).toUpperCase();
  }

  /**
   * El nombre de la persona, o vacío. Una cuenta sin nombre ni apellido (la de plataforma en producción) trae
   * un nombre completo en blanco: sin el trim, el avatar salía como un círculo vacío en vez de usar el correo.
   */
  private nombreDe(u: { nombreCompleto?: string; firstName?: string; lastName?: string } | null): string {
    const completo = (u?.nombreCompleto ?? '').trim();
    return completo || [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim();
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  totalCambiosPendientes(): number {
    const resumen = this.offlineSummary();
    return resumen ? resumen.pendientes + resumen.rechazadas : 0;
  }

  hayCambiosPorSincronizar(): boolean {
    return this.totalCambiosPendientes() > 0;
  }

  async logout(): Promise<void> {
    // Con PIN, salir deja la tablet sin nadie que pueda entrar (hace falta el usuario y la clave de la cuenta): se avisa antes,
    // porque casi siempre lo que se quiere es pasarle la tablet a otro mecánico.
    if (this.operador.activa()) {
      this.salidaTabletOpen.set(true);
      return;
    }
    await this.salirConRevision();
  }

  /** El mecánico confirmó que quiere cerrar la sesión de la cuenta de la tablet. */
  async confirmarSalidaDeLaTablet(): Promise<void> {
    this.salidaTabletOpen.set(false);
    await this.salirConRevision();
  }

  private async salirConRevision(): Promise<void> {
    if (this.logoutSyncing()) return;
    try {
      const resumen = await this.offline.antesDeSalir();
      this.offlineSummary.set(resumen);
      if (resumen.pendientes + resumen.rechazadas + resumen.borradores > 0) {
        this.logoutMessage.set('Si sales de todas formas, no se borra nada: quedan guardados para tu usuario en esta tablet y otros usuarios no los ven.');
        this.logoutDialogOpen.set(true);
        return;
      }
    } catch {
      this.offlineSummary.set(null);
      this.logoutMessage.set('No pudimos revisar los cambios locales. Si sales, los datos se conservarán en esta tablet.');
      this.logoutDialogOpen.set(true);
      return;
    }
    await this.finalizarLogout();
  }

  async sincronizarYSalir(): Promise<void> {
    if (this.logoutSyncing()) return;
    this.logoutSyncing.set(true);
    this.logoutMessage.set('');
    try {
      await this.offline.reintentarRechazadas();
      const resumen = await this.offline.antesDeSalir();
      this.offlineSummary.set(resumen);
      const restantes = resumen.pendientes + resumen.rechazadas + resumen.borradores;
      if (restantes > 0) {
        const cambios = resumen.pendientes + resumen.rechazadas;
        this.logoutMessage.set(
          `${cambios === 1 ? 'Queda' : 'Quedan'} ${cambios} ${cambios === 1 ? 'cambio' : 'cambios'} sin sincronizar y ${resumen.borradores} ${resumen.borradores === 1 ? 'borrador' : 'borradores'}. Puedes seguir trabajando o salir conservándolos para tu usuario.`,
        );
        return;
      }
      await this.finalizarLogout();
    } catch {
      this.logoutMessage.set('No pudimos sincronizar todavía. Tus cambios siguen guardados en esta tablet.');
    } finally {
      this.logoutSyncing.set(false);
    }
  }

  seguirTrabajando(): void {
    this.logoutDialogOpen.set(false);
    this.logoutMessage.set('');
  }

  async salirConPendientes(): Promise<void> {
    if (this.logoutSyncing()) return;
    await this.finalizarLogout();
  }

  private async finalizarLogout(): Promise<void> {
    this.logoutDialogOpen.set(false);
    this.notificacionService.detenerPolling();
    this.logoService.reset();
    // El contador de solicitudes es de esta sesión: si queda, el siguiente que
    // entre en este navegador lo hereda hasta la primera navegación.
    this.solicitudesPendientes.limpiar();
    this.solicitudesWebPendientes.limpiar();
    this.cotizacionesPorValorar.limpiar();
    this.ordenesEnRecepcion.limpiar();
    // La base offline de este usuario se cierra antes de soltar la sesión, y
    // se borra solo si no le queda nada sin mandar (#864). Si falla, la sesión
    // se cierra igual: lo local queda guardado para cuando vuelva a entrar.
    // Sin navegación propia: `logout()` ya lleva al login que corresponde al
    // rol. Forzarla acá era una segunda navegación compitiendo con la suya.
    try {
      await this.offline.alCerrarSesion();
    } catch {
      // Si IndexedDB falla, AuthService no elimina esa base; cerrar sesión
      // igualmente y dejar los datos locales para recuperar al volver.
    } finally {
      this.authService.logout();
    }
  }
}
